import { motion } from "framer-motion";
import { Phone, StickyNote, Bell, Calendar, MessageCircle, FileSpreadsheet, Contact, ArrowRight } from "lucide-react";

const scatteredApps = [
  { name: "Phone",     Icon: Phone,            bg: "#1E9B55", fg: "white",  rotate: -6, delay: 0.0 },
  { name: "Notes",     Icon: StickyNote,       bg: "#FFCD3C", fg: "#8B5E00", rotate: 4,  delay: 0.05 },
  { name: "Reminders", Icon: Bell,             bg: "#F87171", fg: "white",  rotate: -3, delay: 0.1 },
  { name: "Calendar",  Icon: Calendar,         bg: "#E5EAFF", fg: "#1E3AC4", rotate: 5,  delay: 0.15 },
  { name: "WhatsApp",  Icon: MessageCircle,    bg: "#25D366", fg: "white",  rotate: -5, delay: 0.2 },
  { name: "Sheets",    Icon: FileSpreadsheet,  bg: "#0F9D58", fg: "white",  rotate: 3,  delay: 0.25 },
  { name: "Contacts",  Icon: Contact,          bg: "#F1E7FF", fg: "#7C3AED", rotate: -4, delay: 0.3 },
];

const BIZ_LOGO =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_lead-capture-156/artifacts/6n67rx1z_logo.png";

export default function Replaces() {
  return (
    <section
      id="replaces"
      className="relative py-24 md:py-32 border-t border-black/5 overflow-hidden"
      data-testid="replaces-section"
    >
      {/* Ambient blobs */}
      <div
        className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-40 pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(248,113,113,0.28), transparent 70%)" }}
      />
      <div
        className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-40 pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(30,58,196,0.28), transparent 70%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl mb-14 md:mb-20">
          <div className="overline mb-4">The switch</div>
          <h2 className="font-display text-4xl md:text-6xl font-medium leading-[0.95] tracking-tight text-[color:var(--ink)]">
            You&apos;re already using
            <span className="gradient-text" style={{ fontFamily: "var(--font-serif)", fontStyle: "italic" }}>
              {" "}
              seven apps
            </span>{" "}
            for one job.
          </h2>
          <p className="mt-6 text-[color:var(--ink-soft)] text-lg leading-relaxed max-w-xl">
            A call in Phone. A note in Notes. A ping in Reminders. A number in Contacts. A quote on WhatsApp. A total in Sheets. And still, something slips.
          </p>
        </div>

        <div className="grid md:grid-cols-12 gap-8 md:gap-6 items-center">
          {/* LEFT — Scattered apps */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="md:col-span-6 relative"
            data-testid="replaces-scattered"
          >
            <div className="text-[color:var(--ink-mute)] text-xs uppercase tracking-[0.24em] mb-6">Right now</div>
            <div className="grid grid-cols-4 sm:grid-cols-4 gap-3 md:gap-4 relative">
              {scatteredApps.map((a, i) => (
                <motion.div
                  key={a.name}
                  initial={{ opacity: 0, y: 20, rotate: 0 }}
                  whileInView={{ opacity: 1, y: 0, rotate: a.rotate }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, delay: a.delay, ease: [0.16, 1, 0.3, 1] }}
                  className="relative flex flex-col items-center"
                  data-testid={`replaces-app-${a.name.toLowerCase()}`}
                >
                  <div
                    className="w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center shadow-lg"
                    style={{ background: a.bg }}
                  >
                    <a.Icon className="w-6 h-6 md:w-7 md:h-7" style={{ color: a.fg }} strokeWidth={2} />
                  </div>
                  <div className="mt-2 text-[10px] md:text-xs text-[color:var(--ink)]/80 font-medium">{a.name}</div>

                  {/* Crossed-out marker */}
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.4, delay: 0.9 + i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-[color:var(--line-strong)] shadow-sm flex items-center justify-center"
                  >
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="#B91C1C" strokeWidth="2" strokeLinecap="round">
                      <line x1="2" y1="2" x2="8" y2="8" />
                      <line x1="8" y1="2" x2="2" y2="8" />
                    </svg>
                  </motion.div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 1.2 }}
              className="mt-8 text-[color:var(--ink-mute)] text-sm"
            >
              …and one contact still ends up in the wrong place.
            </motion.div>
          </motion.div>

          {/* CENTER — Arrow */}
          <div className="md:col-span-1 flex justify-center py-6 md:py-0">
            <motion.div
              initial={{ opacity: 0, scale: 0.7 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="w-12 h-12 rounded-full bg-[color:var(--ink)] flex items-center justify-center shadow-lg rotate-90 md:rotate-0"
            >
              <ArrowRight className="w-5 h-5 text-white" strokeWidth={2.5} />
            </motion.div>
          </div>

          {/* RIGHT — BizBinder tile */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
            className="md:col-span-5 relative"
            data-testid="replaces-bizbinder"
          >
            <div className="text-[color:var(--ink-mute)] text-xs uppercase tracking-[0.24em] mb-6">With BizBinder</div>

            <div className="relative rounded-3xl bg-white border border-[color:var(--line)] p-8 md:p-10 shadow-xl overflow-hidden">
              {/* Colored glow behind logo */}
              <div
                className="absolute -top-24 -right-24 w-64 h-64 rounded-full blur-3xl opacity-50 pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(30,58,196,0.35), transparent 70%)" }}
              />
              <div
                className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full blur-3xl opacity-40 pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(232,90,42,0.35), transparent 70%)" }}
              />

              <div className="relative flex items-start gap-4">
                <img src={BIZ_LOGO} alt="BizBinder" className="w-16 h-16 rounded-2xl shadow-md shadow-[#1E3AC4]/30" />
                <div>
                  <div className="font-display text-2xl md:text-3xl font-medium tracking-tight gradient-text">
                    BizBinder
                  </div>
                  <div className="text-[color:var(--ink-mute)] text-xs uppercase tracking-widest mt-1">One app</div>
                </div>
              </div>

              <div className="relative mt-8 grid grid-cols-2 gap-x-4 gap-y-2.5">
                {[
                  "Contacts",
                  "Groups & tags",
                  "Voice notes",
                  "Follow-ups",
                  "Card scanner",
                  "Call &amp; WhatsApp",
                  "Global search",
                  "Cloud sync",
                ].map((f, i) => (
                  <motion.div
                    key={f}
                    initial={{ opacity: 0, x: -8 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.4, delay: 1.1 + i * 0.05 }}
                    className="flex items-center gap-2 text-[color:var(--ink)] text-sm"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--accent)] shrink-0" />
                    <span dangerouslySetInnerHTML={{ __html: f }} />
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: 1.6 }}
                className="relative mt-8 pt-6 border-t border-[color:var(--line)] text-[color:var(--ink-soft)] text-sm"
              >
                Everything in one place. Nothing slips.
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
